// The product catalog: every open dataset, data product and service in one list, with its tier,
// what it is built on, where it lives, which entitlement unlocks it and which countries it serves.
// Product cards, the Status section and the route guard all read this; nothing else keeps its own
// grouping. Pure: no React, router, Firebase or service imports.
import { CURRENT_ENV } from './api';
import type { AppEnv } from './api';
import { COUNTRY_LIST } from './countries';
import type { CountryCode } from './countries';
import { odmProjectSelectable, odmSelectable } from '../utils/odmSelection';
import type { SelectedArea } from '../utils/odmSelection';
import { PRODUCT_START_PATHS } from '../utils/productPaths';
import { DATA_PRODUCT_ENTITLEMENT_KEYS, ENTITLEMENT_ALIASES, areaSupport, countriesWith, realtimeSupports } from './products';

export type Tier = 'open' | 'data' | 'service';
export type CatalogId =
  | 'population'
  | 'workplaces'
  | 'boundaries'
  | 'osmRoads'
  | 'trafficCounters'
  | 'activities'
  | 'odm'
  | 'realtime'
  | 'flow'
  | 'emergency'
  | 'traffic';

export interface CatalogEntry {
  id: CatalogId;
  tier: Tier;
  /** "Telcofy Emergency", "Real Time feed", "Population". */
  title: string;
  blurb: string;
  /** Where the product starts; null = placeholder (no page yet). */
  route: string | null;
  /** The GET /users/:uid/products key that unlocks it; null = free (open data). */
  entitlementKey: string | null;
  /** For a service, the products it composes; [] for products. */
  builtOn: CatalogId[];
  /** Which countries it serves: derived from the country and product registries, or listed. */
  countries: 'registry' | CountryCode[];
  status: 'live' | 'draft' | 'planned';
  /** Open data only. */
  source?: { name: string; url: string; licence: string; attribution: string };
  /** False = not offered as a card of its own (a service wraps it). Default true. */
  listed?: boolean;
}

const SSB_COLLECTIONS = 'https://kart.ssb.no/api/core/v1/ogc/features/collections';
const ssb = (collection: string): NonNullable<CatalogEntry['source']> => ({
  name: 'Statistisk sentralbyrå (SSB)',
  url: `${SSB_COLLECTIONS}/${collection}`,
  licence: 'NLOD',
  attribution: 'Statistisk sentralbyrå (SSB)',
});

export const REALTIME_ROUTE = '/monitor-areas-select';
export const EMERGENCY_ROUTE = '/emergency';

export const CATALOG: readonly CatalogEntry[] = [
  {
    id: 'emergency',
    tier: 'service',
    title: 'Telcofy Emergency',
    blurb: 'Follow crowds and visitors across your areas during events and emergencies.',
    route: EMERGENCY_ROUTE,
    entitlementKey: 'emergency',
    builtOn: ['realtime', 'population', 'workplaces'],
    countries: 'registry',
    status: 'draft',
  },
  {
    id: 'traffic',
    tier: 'service',
    title: 'Telcofy Traffic',
    blurb: 'Traffic past any road point, by hour and direction.',
    route: PRODUCT_START_PATHS.flow,
    entitlementKey: DATA_PRODUCT_ENTITLEMENT_KEYS.flow,
    builtOn: ['odm', 'flow'],
    countries: 'registry',
    status: 'live',
  },
  {
    id: 'activities',
    tier: 'data',
    title: 'Activities',
    blurb: 'Analyze location-based activity patterns.',
    route: PRODUCT_START_PATHS.activities,
    entitlementKey: DATA_PRODUCT_ENTITLEMENT_KEYS.activities,
    builtOn: [],
    countries: 'registry',
    status: 'live',
  },
  {
    id: 'odm',
    tier: 'data',
    title: 'Origin–Destination',
    blurb: 'Explore movement between two points.',
    route: PRODUCT_START_PATHS.odm,
    entitlementKey: DATA_PRODUCT_ENTITLEMENT_KEYS.odm,
    builtOn: [],
    countries: 'registry',
    status: 'live',
  },
  {
    id: 'flow',
    tier: 'data',
    title: 'Flow',
    blurb: 'Traffic past any road point, by hour and direction.',
    route: PRODUCT_START_PATHS.flow,
    entitlementKey: DATA_PRODUCT_ENTITLEMENT_KEYS.flow,
    builtOn: [],
    countries: 'registry',
    status: 'live',
    listed: false, // sold as Telcofy Traffic
  },
  {
    id: 'realtime',
    tier: 'data',
    title: 'Real Time feed',
    blurb: 'Monitor real-time data dashboards.',
    route: REALTIME_ROUTE,
    entitlementKey: 'realtime',
    builtOn: [],
    countries: 'registry',
    status: 'live',
  },
  {
    id: 'population',
    tier: 'open',
    title: 'Population',
    blurb: 'Residents on a 250 m grid.',
    route: null,
    entitlementKey: null,
    builtOn: [],
    countries: 'registry',
    status: 'planned',
    source: ssb('befolkning_250m_2025'),
  },
  {
    id: 'workplaces',
    tier: 'open',
    title: 'Workplaces',
    blurb: 'Employees at establishments, on a 250 m grid.',
    route: null,
    entitlementKey: null,
    builtOn: [],
    countries: 'registry',
    status: 'planned',
    source: ssb('bedrifter_250m_2025'),
  },
  {
    id: 'boundaries',
    tier: 'open',
    title: 'Administrative boundaries',
    blurb: 'Counties and municipalities as polygons.',
    route: null,
    entitlementKey: null,
    builtOn: [],
    countries: 'registry',
    status: 'planned',
  },
  {
    id: 'osmRoads',
    tier: 'open',
    title: 'OSM roads',
    blurb: 'The road network from OpenStreetMap.',
    route: null,
    entitlementKey: null,
    builtOn: [],
    countries: 'registry',
    status: 'planned',
  },
  {
    id: 'trafficCounters',
    tier: 'open',
    title: 'Traffic counters',
    blurb: 'Vehicle counts from public road sensors.',
    route: null,
    entitlementKey: null,
    builtOn: [],
    countries: 'registry',
    status: 'planned',
  },
];

export const CATALOG_IDS: readonly CatalogId[] = CATALOG.map((e) => e.id);

const BY_ID: ReadonlyMap<CatalogId, CatalogEntry> = new Map(CATALOG.map((e) => [e.id, e]));

export function catalogEntry(id: CatalogId): CatalogEntry {
  const entry = BY_ID.get(id);
  if (!entry) throw new Error(`Unknown catalog entry: ${id}`);
  return entry;
}

/** Entries of one tier, in catalog order. Unlisted entries (a service wraps them) are left out. */
export function listedIn(tier: Tier): CatalogEntry[] {
  return CATALOG.filter((e) => e.tier === tier && e.listed !== false);
}

// ---------------------------------------------------------------------------------------------
// Countries
// ---------------------------------------------------------------------------------------------

function registryCountries(id: CatalogId): CountryCode[] {
  switch (id) {
    case 'activities':
    case 'odm':
    case 'flow':
      return countriesWith(id);
    case 'realtime':
      return COUNTRY_LIST.filter((c) => c.realtime).map((c) => c.code);
    case 'population':
    case 'workplaces':
      return COUNTRY_LIST.filter((c) => c.ssbOpenData).map((c) => c.code);
    default:
      return [];
  }
}

/** The countries an entry serves. A service serves the countries all the products it is built on serve. */
export function catalogCountries(entry: CatalogEntry): CountryCode[] {
  if (entry.countries !== 'registry') return [...entry.countries];
  if (entry.builtOn.length > 0) {
    const sets = entry.builtOn.map((id) => catalogCountries(catalogEntry(id)));
    return COUNTRY_LIST.map((c) => c.code).filter((c) => sets.every((s) => s.includes(c)));
  }
  return registryCountries(entry.id);
}

// ---------------------------------------------------------------------------------------------
// Entitlements
// ---------------------------------------------------------------------------------------------

/** What GET /users/:uid/products returns, keyed by product key. */
export type Entitlements = Readonly<Record<string, { enabled?: boolean } | undefined>>;

/** Is this key on in the entitlements? Accepts every spelling the key has (ENTITLEMENT_ALIASES). */
export function hasEntitlementKey(key: string, entitlements: Entitlements): boolean {
  const keys = ENTITLEMENT_ALIASES[key] ?? [key];
  return keys.some((k) => !!entitlements[k] && entitlements[k]?.enabled !== false);
}

/**
 * May this account use the entry? Open data is always open. Otherwise its own key must be on. For
 * the demo period a service also counts as entitled when every product it is built on is: current
 * customers who have Real Time keep seeing Emergency until it is sold on its own key. That fallback
 * is off for Emergency in prod: there it needs its own key. Other services (Traffic, which is live)
 * keep the fallback in every env. Test and staff accounts get every key from the backend (`isTest`),
 * so nothing here special-cases them. `env` is a parameter so tests can try both.
 */
export function isEntitled(entry: CatalogEntry, entitlements: Entitlements, env: AppEnv = CURRENT_ENV): boolean {
  if (entry.tier === 'open') return true;
  if (entry.entitlementKey !== null && hasEntitlementKey(entry.entitlementKey, entitlements)) return true;
  if (entry.tier === 'service' && entry.builtOn.length > 0) {
    if (entry.id === 'emergency' && env === 'prod') return false;
    return entry.builtOn.every((id) => isEntitled(catalogEntry(id), entitlements, env));
  }
  return false;
}

// ---------------------------------------------------------------------------------------------
// Which saved areas a product can use
// ---------------------------------------------------------------------------------------------

/** What the rules need to know about a saved area. */
export interface UsableArea {
  id: string;
  type: string;
  ids?: string[];
  country_code?: string | null;
}

export interface AreaUse {
  /** Can the product use this area in principle (type and country), whatever else is picked? */
  supported: boolean;
  /** May it be picked now, given what is already selected? */
  ok: boolean;
  /** Why not; null when ok. */
  reason: string | null;
}

const NOT_FOR_REALTIME = 'Not supported for Real Time';

/**
 * The one rule for "may this saved area be used for this product". It wraps areaSupport (Activities,
 * Flow, Origin-Destination), realtimeSupports (Real Time and Emergency, which follows it) and
 * odmSelectable (Origin-Destination also wants area ids and one area type per selection).
 * `selection` is what is already picked; only Origin-Destination looks at it.
 */
export function canUse(product: CatalogId, area: UsableArea, selection: readonly SelectedArea[] = []): AreaUse {
  switch (product) {
    case 'activities':
    case 'flow':
    case 'traffic': {
      const p = product === 'traffic' ? 'flow' : product;
      const status = areaSupport(area, p);
      return status.ok
        ? { supported: true, ok: true, reason: null }
        : { supported: false, ok: false, reason: status.reason };
    }
    case 'odm': {
      const status = areaSupport(area, 'odm');
      if (!status.ok) return { supported: false, ok: false, reason: status.reason };
      const pick = odmSelectable({ ...area, name: '' }, selection);
      return pick.ok ? { supported: true, ok: true, reason: null } : { supported: true, ok: false, reason: pick.reason };
    }
    case 'realtime':
    case 'emergency':
      return realtimeSupports(area)
        ? { supported: true, ok: true, reason: null }
        : { supported: false, ok: false, reason: NOT_FOR_REALTIME };
    default:
      return { supported: false, ok: false, reason: `${catalogEntry(product).title} does not work on saved areas.` };
  }
}

/**
 * May a project be picked as a whole? Origin-Destination has its own rule (one area type, picked
 * whole or not at all). For every other product it is the members the product can use on their
 * own; a project with none is refused.
 */
export function canUseProject<T extends UsableArea & { name: string }>(
  product: CatalogId,
  members: readonly T[],
  selection: readonly SelectedArea[] = [],
): { ok: true; areas: T[] } | { ok: false; reason: string } {
  if (product === 'odm') return odmProjectSelectable(members, selection);
  const areas = members.filter((m) => canUse(product, m, []).ok);
  return areas.length > 0
    ? { ok: true, areas }
    : { ok: false, reason: `No area in this project can be used for ${catalogEntry(product).title}` };
}
